<template>
  <section class="faq-section" id="answers">
    <v-container class="faq-container">
      <!-- Section Header -->
      <div class="faq-header animate-block">
        <div class="label-area">
          <span class="dot-label">●</span>
          <span class="section-label">{{ $t('faq.label') }}</span>
        </div>
        <div class="title-area">
          <p class="section-title">
            {{ $t('faq.titleLine1') }}
            <br />
            <span class="title-highlight">{{ $t('faq.titleHighlight') }}</span>
            <br />
            {{ $t('faq.titleLine2') }}
          </p>
        </div>
      </div>

      <!-- Service Tabs -->
      <div class="service-tabs animate-block">
        <div class="select-service-area">
          <span class="select-service-label">{{ $t('faq.selectService') }}</span>
        </div>
        <div class="tab-buttons dropdown-wrapper">
          <button class="tab-plus-btn" @click="toggleDropdown">
            <span class="plus-text">+</span>
          </button>
          <button class="tab-service-btn" @click="toggleDropdown">
            <span>{{ selectedCategory.label }}</span>
          </button>
          <transition name="dropdown-fade">
            <ul v-if="dropdownOpen" class="dropdown-menu">
              <li
                v-for="cat in categories"
                :key="cat.key"
                class="dropdown-item"
                :class="{ active: selectedCategoryKey === cat.key }"
                @click="selectCategory(cat.key)"
              >
                {{ cat.label }}
              </li>
            </ul>
          </transition>
        </div>
      </div>

      <!-- FAQ List -->
      <div class="faq-list">
        <div class="separator-line"></div>

        <template v-for="(item, index) in filteredFaqItems" :key="index">
          <div class="faq-item animate-block" @click="toggleItem(index)">
            <div class="faq-question-col">
              <p class="faq-question">{{ item.question }}</p>
            </div>
            <div class="faq-answer-col">
              <div class="faq-answer">
                <p v-for="(paragraph, i) in item.answer" :key="i" class="answer-text">
                  {{ paragraph }}
                </p>
                <ul v-if="item.list && item.list.length" class="answer-list">
                  <li v-for="(listItem, j) in item.list" :key="j" class="answer-list-item">
                    {{ listItem }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="separator-line animate-block"></div>
        </template>
      </div>

      <!-- Back to Services Link -->
      <div class="back-link-area">
        <a href="#services" class="back-link">{{ $t('faq.backLink') }}</a>
      </div>
    </v-container>
  </section>
</template>

<script>
import { animateOnScroll } from '@/mixins/animateOnScroll'

export default {
  name: 'FaqSection',
  mixins: [animateOnScroll],
  data() {
    return {
      dropdownOpen: false,
      selectedCategoryKey: 'all'
    }
  },
  computed: {
    categories() {
      return this.$tm('faq.categories')
    },
    selectedCategory() {
      return this.categories.find(c => c.key === this.selectedCategoryKey) || this.categories[0]
    },
    filteredFaqItems() {
      const items = this.$tm('faq.items')
      if (this.selectedCategoryKey === 'all') return items
      return items.filter(item => item.category === this.selectedCategoryKey)
    }
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },
    selectCategory(key) {
      this.selectedCategoryKey = key
      this.dropdownOpen = false
    },
    handleClickOutside(e) {
      if (!this.$el.querySelector('.dropdown-wrapper')?.contains(e.target)) {
        this.dropdownOpen = false
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.faq-section {
  scroll-margin-top: 80px;
  background-color: #e4e4e4;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 0;
}

.faq-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
}

/* Header */
.faq-header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
}

.label-area {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dot-label {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 30px;
  color: rgb(46, 46, 46);
}

.section-label {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 30px;
  color: rgb(46, 46, 46);
}

.title-area {
  flex: 1;
  min-width: 0;
  margin-left: auto;
  max-width: 55%;
}

.section-title {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 41.6px;
  color: rgb(46, 46, 46);
  margin: 0;
}

.title-highlight {
  color: rgb(28, 165, 190);
}

/* Service Tabs */
.service-tabs {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  width: 100%;
  position: relative;
  z-index: 10;
}

.select-service-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.select-service-label {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 30px;
  color: rgb(28, 165, 190);
  text-decoration: underline;
}

.dropdown-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

.tab-plus-btn {
  width: 38px;
  height: 38px;
  background-color: #1ca5be;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  transition: background-color 0.2s ease;
}

.tab-plus-btn:hover {
  background-color: #1890a6;
}

.plus-text {
  font-family: "IBM Plex Mono", monospace;
  font-size: 20px;
  font-weight: 300;
  letter-spacing: -0.01em;
  line-height: 28.6px;
  color: rgb(241, 241, 241);
  text-transform: uppercase;
}

.tab-service-btn {
  height: 38px;
  min-width: 260px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  padding: 0 14px;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 24px;
  color: rgb(103, 103, 103);
  text-align: left;
  white-space: nowrap;
  transition: border-color 0.2s ease;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 300px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 4px;
  z-index: 100;
  max-height: 320px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 8px 12px;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgb(46, 46, 46);
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.dropdown-item:hover {
  background-color: rgba(28, 165, 190, 0.08);
}

.dropdown-item.active {
  background-color: rgba(28, 165, 190, 0.12);
  color: rgb(28, 165, 190);
  font-weight: 500;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* FAQ List */
.faq-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.separator-line {
  width: 100vw;
  height: 1px;
  background-color: #0000001A;
  position: relative;
  left: 0%;
}

.faq-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
}

.faq-question-col {
  width: 30%;
  flex-shrink: 0;
}

.faq-question {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 25px;
  color: rgb(103, 103, 103);
  margin: 0;
}

.faq-answer-col {
  flex: 1;
  min-width: 0;
}

.faq-answer {
  width: 100%;
}

.answer-text {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 24px;
  color: rgb(77, 77, 77);
  margin: 0;
}

.answer-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
}

.answer-list-item {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 24px;
  color: rgb(77, 77, 77);
  padding-left: 16px;
  position: relative;
  margin-bottom: 2px;
}

.answer-list-item::before {
  content: "—";
  position: absolute;
  left: 0;
  color: rgb(28, 165, 190);
}

/* Back Link */
.back-link-area {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
}

.back-link {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 30px;
  color: rgb(28, 165, 190);
  text-decoration: underline;
  transition: color 0.2s ease;
}

.back-link:hover {
  color: rgb(18, 129, 149);
}

/* Responsive */

@media (max-width: 960px) {
  .faq-section {
    padding: 60px 0;
  }

  .faq-header {
    flex-direction: column;
    gap: 24px;
  }

  .label-area {
    width: 100%;
  }

  .title-area {
    width: 100%;
    max-width: none;
    margin-left: 0;
  }

  .section-title {
    font-size: 26px;
    line-height: 34px;
  }

  .service-tabs {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .dropdown-wrapper {
    width: 100%;
    min-width: unset;
  }

  .faq-item {
    flex-direction: column;
    gap: 16px;
  }

  .faq-question-col {
    width: 100%;
  }

  .faq-answer-col {
    width: 100%;
  }

  .faq-question {
    font-size: 18px;
    line-height: 23px;
  }
}

@media (max-width: 600px) {
  .faq-section {
    padding: 40px 0;
  }

  .section-title {
    font-size: 22px;
    line-height: 30px;
  }

  .faq-question {
    font-size: 16px;
    line-height: 22px;
  }

  .answer-text {
    font-size: 13px;
    line-height: 20px;
  }

  .answer-list-item {
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
